import { Grid, makeStyles, TextField } from "@material-ui/core";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import anniversaryImage from "../../img/Employee_aniversary_background.png"
import { Button } from "@mui/material";
import CanvasWrapper from "./CanvasWrapper";

export default function AnniversaryTemplateBuilder() {
    const classes = useStyles();
    const [yearsNumber, setYearsNumber] = useState<number>(0);
    const [personFullName, setPersonFullName] = useState<string>("")
    const [imgWidth, setImgWidth] = useState(0)
    const imageRef = useRef<any>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    const fontFamily = "Nunito"
    const shadowColor = "#0000005E"

    useEffect(() => {
      loadCanvasSize();
      renderCanvasText();
    },[imageRef.current])

    useEffect(() => {
      clearCanvas();
      renderCanvasText();
    },[yearsNumber, personFullName])

    const getCanvas2DContext = () => canvasRef.current!.getContext('2d');

    const loadCanvasSize = () => {
      const canvas = getCanvas2DContext()!.canvas;
      canvas.width = imageRef.current.width;
      canvas.height = imageRef.current.height;
    }

    const canvasCommonProperties = () => {
      const canvas = getCanvas2DContext();
      canvas!.fillStyle = "white";
      canvas!.shadowColor = shadowColor;
      canvas!.shadowOffsetX = 5;
      canvas!.shadowOffsetY = 5;
      canvas!.shadowBlur = 10;
      canvas!.textAlign = "center";
    }

    const renderCanvasText = () => {
      renderYearsNumber();
      renderYearsText();   
      renderAnniversaryText()
      const marginTop = renderPersonName();
      renderAnniversaryDescriptionText(marginTop);
    }

    const renderYearsNumber = () => {
      const canvas = getCanvas2DContext();
      canvas!.font = `BOLD 600px ${fontFamily}`;
      canvasCommonProperties();

      canvas!.fillText(yearsNumber as unknown as string, 250, canvasRef.current!.height / 1.5);
    }

    const renderYearsText = () => {
      const canvas = getCanvas2DContext();
      const yearsText = `${"year" + (yearsNumber > 1 ? "s" : "")}`;
      const fontSize = 125

      canvas!.font = `1000 italic ${fontSize}px ${fontFamily}`;
      canvasCommonProperties()

      canvas!.fillText(yearsText, 250, canvasRef.current!.height / 1.25);
    }

    const renderAnniversaryText = () => {
      const canvas = getCanvas2DContext();
      const happyText = "HAPPY";
      const anniversaryText = "ANNIVERSARY";
      const fontSize = 125

      canvas!.font = `900 italic ${fontSize}px ${fontFamily}`;
      canvasCommonProperties();
      canvas!.fillText(happyText, (canvasRef.current!.width / 1.65), (canvasRef.current!.height / 2) / 2);
      canvas!.fillText(anniversaryText, (canvasRef.current!.width / 1.65), (canvasRef.current!.height / 2) / 2 + 110);
    }
 
    const renderPersonName : () => number  = () => {
      const canvas = getCanvas2DContext();
      const characterLimit = 21;
      const fontSize = 90;

      canvas!.font = `800 italic ${fontSize}px ${fontFamily}`;
      canvasCommonProperties()

      let personName = [personFullName];
      if(personFullName.length > characterLimit ){
        personName = personFullName.split(" ");
      }

      if(personName.length >= 3 && (personName[personName.length - 1].length + personName[personName.length - 2].length) <= characterLimit){
        const lastName = personName.pop();
        personName[personName.length - 1] = personName[personName.length - 1] + " " + lastName
      }

      personName.forEach((name, index) => {
        canvas!.fillText(name, (imageRef.current!.width / 1.65), (((canvasRef.current!.height / 2) / 2 + 220) + (fontSize * index)));
      })

      return fontSize * (personName.length - 1);
    }

    const renderAnniversaryDescriptionText = (topMargin: number) => {
      const canvas = getCanvas2DContext();
      const firstSentence = "Another year that find us";
      const secondSentence = "celebrating shared accomplishments!";
      const fontSize = 45;
      
      canvas!.font = `700 italic ${fontSize}px ${fontFamily}`;
      canvasCommonProperties();

      canvas!.fillText(firstSentence, (canvasRef.current!.width / 1.65), (canvasRef.current!.height / 1.7) + topMargin) ;
      canvas!.fillText(secondSentence, (canvasRef.current!.width / 1.65), (canvasRef.current!.height / 1.7) + fontSize + topMargin);
    }


    const handlePersonNameChange = ({ target: {value}}: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setPersonFullName(value)
    }

    const handleYearsTextChange = ({ target: {value}}: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setYearsNumber(value as unknown as number)
    }

    const clearCanvas = () => {
      const canvas = getCanvas2DContext();
      canvas!.clearRect(0, 0, canvasRef.current!.width, canvasRef.current!.height)
    }

    const prepareSave = () => {
      const img = new Image();
      img.src = anniversaryImage;
      img.addEventListener("load", () => {
        canvasRef.current!.height = img.height;
        canvasRef.current!.width = img.width;
        getCanvas2DContext()!.drawImage(img, 0, 0);
        renderCanvasText();
        saveCanvasAsImage();
      })
    }

    const saveCanvasAsImage = () => {
      const canvas = canvasRef.current;
      const link = document.createElement('a');
      const personName = personFullName.replaceAll(" ", "-");
      link.download = `welcome-image-${personName}.png`;
      link.href = canvas!.toDataURL('image/png'); 
      link.click()
  }

  return (
    <>
      <Grid container style={{ 
            display: 'flex', 
            alignItems: 'flex-start',
            flexDirection: 'row',
            justifyContent: 'space-between',
            padding: '1rem',
            boxSizing: 'inherit',
            boxShadow: 'none',    
            }}>
        <Grid item xs={3}>
          <TextField
            type="number"
            className={classes.textField}
            label="Number of years"
            value={yearsNumber}
            onChange={handleYearsTextChange}
            autoComplete="off"
            size="small"
            />
        </Grid>
        <Grid item xs={9}>
          <TextField
            type="text"
            className={classes.textField}
            label="Person name"
            value={personFullName}
            onChange={handlePersonNameChange}
            autoComplete="off"
            size="small"
            />
        </Grid>
      </Grid>

      <CanvasWrapper width={imgWidth} setWidth={setImgWidth} image={anniversaryImage}>
        <Grid container>
          <Grid className={classes.container} xs={12}>
            <img ref={imageRef} className={classes.backgroundImage} src={anniversaryImage} alt=""/>
            <canvas className={classes.canvas} ref={canvasRef}></canvas>
          </Grid>
        </Grid>
      </CanvasWrapper>

      <Button onClick={prepareSave}>Download</Button>
    </>
  )
}

const useStyles = makeStyles(() => ({
  container: {
    position:"relative",
  },
  backgroundImage:{
    position:"absolute"
  },
  textField: {
    width: "12rem",
    minWidth: "10rem",
  },
  canvas: {
    zIndex:100,
    position:"relative",
  }
}),
{ index: 1 }
);
